import { useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { Customer, HoardOrder, HoardType, useHoardHooks } from '../../../../sales'
import { DeliveryStatus } from '../../../../deliveries'
import { RoutePath, useRoutesHooks } from '../../../../../features/routes'
import { WidgetCard } from '../widget-card/widgetCard'
import { WidgetList, WidgetListRow } from '../widget-list/widgetList'
import { formatAmount, formatNumber, todayStr, toLocalDateStr } from '../../../utils/dashboardUtils'

const DAYS_AHEAD = 30

type Props = {
    loading: boolean
    error: boolean
    hoards: HoardOrder[]
    customers: Customer[]
}

// Hoards still pending delivery whose due date is in the next 30 days (or already passed)
export const ExpiringHoardsWidget = (props: Props) => {
    const { loading, error, hoards, customers } = props

    const { getPendingCredit } = useHoardHooks()
    const { getRoutePath } = useRoutesHooks()
    const navigate = useNavigate()

    const rows = useMemo((): WidgetListRow[] => {
        const today = todayStr()
        const limit = new Date()
        limit.setDate(limit.getDate() + DAYS_AHEAD)
        const limitStr = toLocalDateStr(limit.toISOString())

        return hoards
            .filter(hoard => hoard.dueDate && hoard.deliveryStatus !== DeliveryStatus.DELIVERED)
            .map(hoard => ({ hoard, dueDate: toLocalDateStr(hoard.dueDate) }))
            .filter(({ dueDate }) => dueDate <= limitStr)
            .sort((a, b) => a.dueDate.localeCompare(b.dueDate))
            .map(({ hoard, dueDate }) => {
                const isOverdue = dueDate < today
                const [, month, day] = dueDate.split('-')
                const isFinancial = hoard.type === HoardType.FINANCIAL
                const pendingUnits = hoard.items.reduce((sum, item) => sum + (item.deliveryStats?.pending || 0), 0)
                const customerName = customers.find(customer => customer.id === hoard.customerId)?.name || '-'
                return {
                    key: hoard.id,
                    label: `#${hoard.code} ${customerName}`,
                    detail: isOverdue ? `venció ${day}/${month}` : `vence ${day}/${month}`,
                    value: isFinancial ? formatAmount(Math.max(0, getPendingCredit(hoard))) : `${formatNumber(pendingUnits)} u.`,
                    valueKind: isOverdue ? 'negative' : 'warning',
                    onClick: () => navigate(getRoutePath(RoutePath.HOARDS, hoard.id))
                } as WidgetListRow
            })
    }, [hoards, customers])

    return (
        <WidgetCard
            title="Acopios por vencer (30 días)"
            loading={loading}
            error={error}
            empty={!loading && !error && rows.length === 0}
            emptyLabel="No hay acopios por vencer"
            linkLabel="Ver acopios"
            linkPath={RoutePath.HOARDS}
        >
            <WidgetList rows={rows} />
        </WidgetCard>
    )
}
